import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { MemberService } from 'app/cd/services/member-service';
import { apiCall } from 'app/shared/lib/api/api';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import * as CD from 'app/cd/models/essette-api';
import { SummaryLoader } from 'app/cross/services/summary-loader';
import { modelCodeToResourceKey } from 'app/shared/lib/i18n';

@autoinject()
export class MemberNotes extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public memberId: string | null = null;

    protected memberNotes: ReadonlyArray<CD.MemberRollupNote> = [];

    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 25;
    protected modelCodeToResourceKey = modelCodeToResourceKey;
    protected hasCaseRoute = false;
    protected hasAuthRoute = false;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly memberService: MemberService,
        private readonly summaryLoader: SummaryLoader,
        ...rest: any[]) {
        super(...rest);
        this.checkPermissions();
    }

    protected async activate(params: any): Promise<void> {
        await this.loadMemberNotes();
    }

    ////****************** Events ***************************************************////
    protected async reload(): Promise<void> {
        await this.loadMemberNotes();
    }

    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
            if(this.sorting.entries.length == 0) {
                this.sorting.entries = [
                    {
                        reference: 'metadata.created.timestamp',
                        direction: SortDirection.Descending
                    }
                ];
            }
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
        //Call stop Propagation to prevent parent table from reload and losing sort
        event.stopPropagation();
    }

    /** Opens the respective summary */
    protected openSummary(code: string, id: string): void {
        this.summaryLoader.openAttachedToSummary(code, id, this.hasCaseRoute, this.hasAuthRoute);
    }

    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasAuthRoute = this.accountContext.hasRouteAccess('um.auth');
        this.hasCaseRoute = this.accountContext.hasRouteAccess('cm.case');
    }

    /** Returns whether or not a user has permission to open a summary */
    protected showLink(code: string): boolean {
        return this.summaryLoader.hasPermission(this.accountContext, code);
    }

    /** Returns an i18n tooltip string */
    protected attachedToToolTip(code: string): string {
        return this.summaryLoader.attachedToToolTip(code);
    }

    //** Returns a codes i18n description string */
    protected attachedToType(code: string): string {
        return this.summaryLoader.attachedToType(code);
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadMemberNotes(): Promise<void> {
        if(!this.memberId) return;

        const includes: string[] = [
            'attachedTo',
            'attachedTo.type',
            'entered.byUser',
            'category',
            'category.subCategory'
        ];

        const fields: string[] = [
            'attachedTo.description',
            'attachedTo.type.code',
            'category.description',
            'category.subCategory.description',
            'entered.byUser.description',
            'entered.dateTime',
            'text',
        ];

        const options: Partial<IQueryLoadOptions> = {
            includeInactive: false,
            sorting: this.sorting,
            page: this.$pager?.page || 0,
            top: this.pageSize
        };

        try {
            this.state.beginLoad();

            const response = await this.memberService.loadMemberSummaryNotes(this.memberId, options, includes, fields);
            this.memberNotes = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
